import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Download, Link2, ArrowLeft, CheckCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProposalPdfView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bid, setBid] = useState(null);
  const [embedFailed, setEmbedFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  const token = localStorage.getItem('pp_token');
  const ts = Date.now();
  const viewUrl = `${API}/api/bids/${id}/proposal-pdf?token=${token}&mode=view&_t=${ts}`;
  const downloadUrl = `${API}/api/bids/${id}/proposal-pdf?token=${token}&mode=download&_t=${ts}`;

  useEffect(() => {
    // Force recompute totals before viewing PDF to ensure fresh data
    api.post(`/bids/${id}/compute-totals`).catch(() => {});
    api.get(`/bids/${id}`).then(r => setBid(r.data)).catch(() => {});
  }, [id]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(viewUrl).then(() => {
      setCopied(true);
      toast.success('PDF link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `HDH_Proposal_${(bid?.inputs?.customer_name || 'Pool').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const customerName = bid?.inputs?.customer_name || 'Proposal';
  const status = bid?.status || 'draft';
  const total = bid?.totals?.total_price || 0;

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)]" data-testid="pdf-view-page">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-slate-200">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/bids/${id}`)} data-testid="pdf-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Bid
          </Button>
          <div className="h-5 w-px bg-slate-200" />
          <div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#F59E0B]" />
              <h1 className="text-sm font-bold text-[#0F1D32]">{customerName} — Proposal PDF</h1>
              <Badge className={`text-[10px] ${status === 'won' ? 'bg-emerald-50 text-emerald-700' : status === 'sent' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{status}</Badge>
            </div>
            {total > 0 && <p className="text-[11px] text-slate-400 mt-0.5">${total.toLocaleString()}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleCopyLink} className="text-xs" data-testid="pdf-copy-link">
            {copied ? <CheckCircle className="w-3.5 h-3.5 mr-1 text-emerald-500" /> : <Link2 className="w-3.5 h-3.5 mr-1" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.open(viewUrl, '_blank')} className="text-xs" data-testid="pdf-open-tab">
            <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open in New Tab
          </Button>
          <Button size="sm" onClick={handleDownload} className="text-xs bg-[#0F1D32] hover:bg-[#1a2d45] text-white" data-testid="pdf-download">
            <Download className="w-3.5 h-3.5 mr-1" /> Download PDF
          </Button>
        </div>
      </div>

      {/* PDF Embed Area */}
      <div className="flex-1 bg-slate-100 relative">
        {!embedFailed ? (
          <object
            data={viewUrl}
            type="application/pdf"
            className="w-full h-full"
            onError={() => setEmbedFailed(true)}
            data-testid="pdf-embed"
          >
            {/* Fallback if object tag fails */}
            <iframe
              src={viewUrl}
              className="w-full h-full border-0"
              title="Proposal PDF"
              onError={() => setEmbedFailed(true)}
            />
          </object>
        ) : (
          <div className="flex items-center justify-center h-full">
            <Card className="max-w-md w-full p-8 text-center">
              <FileText className="w-12 h-12 text-[#F59E0B] mx-auto mb-4" />
              <h2 className="text-lg font-bold text-[#0F1D32] mb-2">PDF Generated Successfully</h2>
              <p className="text-sm text-slate-500 mb-6">
                Inline PDF viewing is not supported in this browser. Use the buttons above to open or download the file.
              </p>
              <div className="flex flex-col gap-3">
                <Button className="w-full bg-[#0F1D32] hover:bg-[#1a2d45] text-white" onClick={() => window.open(viewUrl, '_blank')}>
                  <ExternalLink className="w-4 h-4 mr-2" /> Open PDF in Browser
                </Button>
                <Button variant="outline" className="w-full" onClick={handleCopyLink}>
                  <Link2 className="w-4 h-4 mr-2" /> Copy PDF Link
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
